import { useCallback, useState } from "react";
import { type SortPayload, useGetList } from "ra-core";

import type { Task } from "../types";
import {
  getTaskDueBucketQueryFilter,
  type TaskDueBucket,
  type TaskFilter,
} from "./taskDueBuckets";

/** Rows fetched per bucket. "Load more" raises the window by the same amount. */
export const TASK_BUCKET_PAGE_SIZE = 25;

export type TaskDueBucketResult = {
  tasks: Task[];
  /** Server-side count for the whole bucket, not just the fetched window. */
  total: number;
  isPending: boolean;
  hasMore: boolean;
  loadMore: () => void;
};

/**
 * One due-date bucket, driven by its own filtered query.
 *
 * Every bucket is a narrow, indexed range on `due_date`, so the list never has
 * to pull the whole task table into the browser to slice it — and the count it
 * reports is the real one rather than "however many rows happened to fit".
 */
export const useTaskDueBucket = (
  bucket: TaskDueBucket,
  filter: TaskFilter,
  sort: SortPayload,
  enabled: boolean,
): TaskDueBucketResult => {
  const [perPage, setPerPage] = useState(TASK_BUCKET_PAGE_SIZE);

  const { data, total, isPending } = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage },
      sort,
      filter: { ...filter, ...getTaskDueBucketQueryFilter(bucket) },
    },
    { enabled },
  );

  const loadMore = useCallback(
    () => setPerPage((current) => current + TASK_BUCKET_PAGE_SIZE),
    [],
  );

  return {
    tasks: data ?? [],
    total: total ?? 0,
    isPending: enabled && isPending,
    hasMore: (total ?? 0) > (data?.length ?? 0),
    loadMore,
  };
};
